import React, { useState, useEffect } from 'react';
import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import { Link, Box, Tooltip, IconButton, Autocomplete, TextField } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LaunchIcon from '@mui/icons-material/Launch';
import { useOptionsService } from '#service/autocomplete-service/hooks';
import DeviceIcon from '#component/icon/device-icon';
import { DEVICE, PROJECT, USER } from '#component/general/list-filter/constants';
import {
  SubComponentContainer,
  Label,
  StyledChip,
  DeviceChip,
  DeviceChipContainer,
  OpenLink,
  CloseBtn,
  DisabledOptionText
} from '#component/general/list-filter/components/general/styles';

export const MultipleSelect = ({
  name,
  label,
  relatedTo,
  onChange,
  searchFields,
  combinedWith,
  fetchOptionsService,
  afterChangeMultipleSelect
}) => {
  const { options, status, ...serviceMethods } =
  useOptionsService(searchFields, fetchOptionsService, { combinedWith });
  const loading = status === 'loading' && options.length === 0;
  const meta = useSelector(s => s.meta, shallowEqual);
  const selected = useSelector(s => s.listFilter?.[relatedTo]?.[name]?.value, shallowEqual);
  const [value, setValue] = useState(selected || []);
  const [inputValue, setInputValue] = useState('');
  const disabledOption = option => option.withoutDirectProjects;
  const dispatch = useDispatch();

  useEffect(() => {
    setValue(selected ?? []);
  }, [selected]);

  const handleChange = (e, newValue) => {
    setValue(newValue);
    onChange(newValue);

    if (afterChangeMultipleSelect) {
      dispatch(afterChangeMultipleSelect(newValue));
    }
  };

  const handleInputChange = (e, newInputValue) => {
    setInputValue(newInputValue);
    if (newInputValue.length >= 2) {
      serviceMethods.fetchOptions(newInputValue);
    } else if (newInputValue === '') {
      serviceMethods.fetchOptions();
    }
  };

  const handleOnOpen = () => {
    serviceMethods.fetchOptions();
  };

  const handleClose = () => {
    serviceMethods.clearOptions();
  };

  const filterOptions = (opts, selectedOptions = []) =>
    opts.filter(o => !selectedOptions.some(s => s.label === o.label));

  const getChip = (option, index, getTagProps) => {
    const { key: tagKey, ...tagProps } = getTagProps({ index });
    const commonProps = {
      tabIndex: -1,
      onClick: e => e.stopPropagation(),
      label: (
        <Link
          href="/"
          color="inherit"
          target="_blank"
          onMouseDown={e => e.stopPropagation()}
        >
          {option.label}
        </Link>
      ),
      size: 'small',
      variant: 'outlined',
      ...tagProps,
      deleteIcon: <CloseIcon />
    };

    switch (name) {
      case DEVICE:
        return (
          <DeviceChipContainer key={option.label}>
            <DeviceChip
              {...commonProps}
              variant="outlined"
              label={
                <>
                  {option.label}
                  <Tooltip title="Open the device in a new tab">
                    <OpenLink
                      href={`${window.location.origin}/device/${option?.data?.id}`}
                      color="inherit"
                      target="_blank"
                      onMouseDown={e => e.stopPropagation()}
                    >
                      <CloseBtn
                        onClick={e => e.stopPropagation()}
                        size="small"
                      >
                        <LaunchIcon
                          fontSize="small"
                          color="action"
                        />
                      </CloseBtn>
                    </OpenLink>
                  </Tooltip>
                </>
              }
              icon={<DeviceIcon
                icon={meta.device?.find(m => m.name === option?.data?.type)?.icon}
                style={{ fontSize: '22px' }}
              />}
              deleteIcon={
                <IconButton
                  size="small"
                  aria-label="Remove device button"
                >
                  <CloseIcon
                    fontSize="small"
                  />
                </IconButton>
              }
            />
          </DeviceChipContainer>
        );
      case PROJECT:
        return (
          <StyledChip
            key={option.label}
            {...commonProps}
            label={React.cloneElement(commonProps.label, {
              href: `${window.location.origin}/project/edit/${option?.data?.id}`
            })}
          />
        );
      case USER:
        return (
          <StyledChip
            key={option.label}
            {...commonProps}
            label={React.cloneElement(commonProps.label, {
              href: `${window.location.origin}/user/${option?.data?.id}`
            })}
          />
        );
      default:
        return (
          <StyledChip
            key={option.label}
            variant="outlined"
            label={option.label}
            size="small"
            {...tagProps}
            deleteIcon={<CloseIcon />}
          />
        );
    }
  };

  return (
    <SubComponentContainer>
      <Label>{label}</Label>
      <Autocomplete
        multiple
        value={value}
        clearOnBlur={true}
        onChange={handleChange}
        inputValue={inputValue}
        style={{ width: '80%' }}
        onInputChange={handleInputChange}
        id={`filter-multipleSelect-${label}`}
        options={options}
        filterOptions={opts => filterOptions(opts, selected)}
        isOptionEqualToValue={(option, v) => option.label === v.label}
        getOptionDisabled={disabledOption}
        loading={loading}
        onOpen={handleOnOpen}
        onClose={handleClose}
        renderTags={(t, getTagProps) =>
          t.map((option, index) => getChip(option, index, getTagProps))}
        renderInput={params =>
          <TextField {...params} label={`${label}`} />}
        renderOption={(props, option) => {
          const { key, ...otherProps } = props;
          return (
            <Box
              component="li"
              key={`${key}_${option?.data?.id}`}
              {...otherProps}
            >
              <span>{option.label}</span>
              {disabledOption(option) && <DisabledOptionText>No projects</DisabledOptionText>}
            </Box>
          );
        }}
      />
    </SubComponentContainer>
  );
};
